import React, {useState, useContext, useEffect, useMemo, useCallback, memo} from "react";
import {useTranslation} from "react-i18next";
// local
import SignContractsItem from "./SignContractsItem";
import {Preloader} from "components/Preloader";
import {PaginationType} from "components/Pagination";
import {hasPermission, scrollToTopClassname, hasUserSubscription} from "helpers/helper";
import {SignContractsContext} from "../SignContractsContext";
import SignContractsHead from "./SignContractsHead";
import {IconFeather} from "components/icons";
import {UpsaleBoxLocalModal} from "components/modals/UpsaleBoxLocalModal";
import {useSavedFilters} from "../hooks";
import {getSignContracts} from "../api/contracts";
import {getOrganizationMembers} from "../api/organization";
import {
  defaultSignContractsParamsSearch,
  signContractStatuses,
  signContractStatuseNames,
  signContractsTabs,
  signContractStatusName,
} from "../constants";
import {hashes, subscriptionSubTabHash} from "constants/tabs.const";
import SignContractsNullStateContent from "./SignContractsNullStateContent";

export const SignContractsTab = memo(() => {
  const {signContractsActiveTab, currentUser} = useContext(SignContractsContext);
  const {t} = useTranslation();

  const [contractsData, setContractsData] = useState(null);
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirtyFilters, setDirtyFilters] = useState(false);

  const canCreate = hasPermission(["CREATE_SIGN_CONTRACT"]);
  const canClickOnContract = hasUserSubscription([
    "SUBSCRIPTION_NAME_08",
    "SUBSCRIPTION_NAME_09",
    "SUBSCRIPTION_NAME_10",
  ]);

  const defaultSignContractsParams = useMemo(() => {
    return {
      ...defaultSignContractsParamsSearch,
      tab: signContractsTabs.find((tab) => tab.id === signContractsActiveTab).requestKey,
    };
  }, [signContractsActiveTab]);

  const signContractStatusesSelectList = useMemo(
    () =>
      [...signContractStatuseNames]
        .slice(1)
        .filter((item) =>
          signContractsActiveTab === 1 ? item !== signContractStatusName.DRAFT : true
        ),
    [signContractsActiveTab]
  );

  const [filters, setSignContractFilter, , isInitialFilter, isInitialFilterParams] =
    useSavedFilters({
      key: `filterParamsForSignContracts-${signContractsActiveTab}`,
      defaultValue: defaultSignContractsParams,
      allowedValues: {
        sort: ["createdAt", "my", "abc"],
        tab: ["created", "sent"],
        status: signContractStatuses,
      },
      allowedKeys: ["sort", "tab", "status", "page"],
      listTypeKeys: ["status"],
      storage: "session",
    });

  const needScroll = useMemo(() => !!filters && dirtyFilters, [filters, dirtyFilters]);

  const filledContracts = useMemo(
    () =>
      contractsData?.contracts?.map((contract) => ({
        ...contract,
        createdMember:
          contract.CreatedParticipant ??
          members?.find((member) => member.id === contract.createdMemberId),
      })),
    [contractsData, members]
  );

  const onChangeFilters = useCallback(
    (name, value) => {
      const params = {...filters, [name]: value, page: name === "page" ? value : 1};
      if (!params?.status?.length) delete params["status"];
      setSignContractFilter(params);
      setDirtyFilters(true);
    },
    [filters, setSignContractFilter]
  );

  useEffect(() => {
    signContractsActiveTab === 0 && !members && getOrganizationMembers().then(setMembers);
  }, [signContractsActiveTab, members]);

  useEffect(() => {
    !!needScroll && scrollToTopClassname("tabs-content");
    !!filters &&
      getSignContracts(filters)
        .then(setContractsData)
        .finally(() => setLoading(false));
  }, [filters, needScroll]);

  useEffect(() => {
    scrollToTopClassname("main-content_wrapper");
  }, []);

  return (
    <div className="pos-r">
      {!!filters && (
        <SignContractsHead
          filterParams={filters}
          statuses={signContractStatusesSelectList}
          clickOnSortFilter={onChangeFilters}
        />
      )}
      {loading && <Preloader addClass="preloader100" />}
      {!loading && (
        <React.Fragment>
          {(!!filledContracts?.length || !isInitialFilterParams || isInitialFilter) &&
            filledContracts?.map((contract) => (
              <SignContractsItem
                key={contract.id}
                contract={contract}
                ownUser={contract?.createdMember?.UserId === currentUser?.id}
              />
            ))}
          <PaginationType
            totalItems={contractsData.totalItems}
            currentPage={contractsData.currentPage}
            countPerPage={9999}
            onPageChange={(value) => onChangeFilters("page", value)}
            addClass="mt-8"
          />
          {!filledContracts?.length && !isInitialFilterParams && (
            <div className="__text-center fz-19 mid-gray mt-8">
              {t("SIGN_CONTRACTS_HIDDEN_BY_FILTERS")}
            </div>
          )}
          {(!canCreate ||
            signContractsActiveTab === 1 ||
            (!filledContracts?.length && isInitialFilterParams && isInitialFilter)) && (
            <SignContractsNullStateContent />
          )}
        </React.Fragment>
      )}

      <UpsaleBoxLocalModal
        additionalShowCondition={!canClickOnContract}
        data={{
          title: "SIGN_CONTRACTS_UPSALE_TITLE",
          buttonIcon: <IconFeather />,
          buttonServiceExtraName: "SIGN_CONTRACTS_UPSALE_SERVICE_BUTTON",
          directSubscriptionLink: `${hashes.SUBSCRIPTION}_${subscriptionSubTabHash.DIGITAL_SIGNING}`,
          priceLabel: "SUBSCRIPTION_TAB_PRICE_FROM",
          priceValue: "CURRENT_PRICE_SUBSCRIPTION_08",
          priceType: "PRICE_MESSAGE_OFFER_SUB_08",
        }}
        extraData={{
          bookButtonUrl: "SIGN_CONTRACTS_UPSALE_BOOK_DEMO_URL",
          infoButtonUrl: "SIGN_CONTRACTS_UPSALE_MORE_INFO_URL",
          videoTitle: "Nöjd kund berättar - Interoc",
          videoUrl: "SIGN_CONTRACTS_UPSALE_VIDEO_URL",
          videoSize: {width: "233", height: "142"},
        }}
        priceBlobAlign="left"
        position={{top: "115px", right: "150px"}}
      />
    </div>
  );
});

export default SignContractsTab;
